import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { PaginateQuery, PaginatorModel, UUID } from 'symbols';
import { OrdersCountModel } from '../../orders/symbols';
import { TemplateExpectBackend, TemplateModel } from '../../pdf-templates/symbols';
import { BasicMetaData, Order, Sort, TYPE_SEARCH } from '../../symbols';
import {
  StandFilterModel,
  StandModel,
  StandModelExpectBackend,
  StandsCountModel,
  StandStatusExpectBackend
} from '../symbols';

@Injectable()
export class StandService {
  public sortDefault$ = new BehaviorSubject({ sort: Sort.CREATED, stand: Order.DESC });

  private readonly apiUrl = '/api/stand';


  constructor(private http: HttpClient) {
  }

  getStandList(filter?: PaginateQuery<StandFilterModel>): Observable<PaginatorModel<StandModel>> {
    const params: { [key: string]: any } = {
      page: filter?.page || 1,
      limit: filter?.limit || 50,
      // усі правила сортування (напр. спершу пріоритетні, потім за датою), а не лише перше
      sortBy: (filter?.sortBy || []).map(([column, order]) => `${column}:${order}`),
      typeSearch: filter?.filter?.typeSearch || TYPE_SEARCH.BY_METADATA
    };


    if (filter?.filter?.typeSearch === TYPE_SEARCH.BY_NUMBER) {
      params['filter.docNumber'] = +filter?.filter?.searchText || '';
    } else {
      params['search'] = filter?.filter?.searchText || '';
    }

    if (filter?.filter?.isToxic) {
      params['filter.isToxic'] = filter?.filter?.isToxic;
    }

    if (filter?.filter?.isDebt) {
      params['filter.isDebt'] = filter?.filter?.isDebt;
    }

    if (filter?.filter?.isHighPriority) {
      params['filter.isHighPriority'] = filter?.filter?.isHighPriority;
    }

    if (filter?.filter?.masterId) {
      params['filter.masterId'] = filter?.filter?.masterId;
    }

    if (filter?.filter?.statusIds?.length) {
      params['filter.statusIds'] = filter?.filter?.statusIds;
    }

    return this.http.get<PaginatorModel<StandModel>>(this.apiUrl, { params });
  }


  getStandListLazy(url: string): Observable<PaginatorModel<StandModel>> {
    return this.http.get<PaginatorModel<StandModel>>(url);
  }

  getStatusList(): Observable<StandStatusExpectBackend[]> {
    return this.http.get(`${this.apiUrl}/status-list`) as Observable<StandStatusExpectBackend[]>;
  }

  getStandById(id: string): Observable<StandModel> {
    return this.http.get<StandModel>(`${this.apiUrl}/stand/${id}`);
  }

  createStand(stand: StandModelExpectBackend): Observable<StandModel> {
    return this.http.post<StandModel>(`${this.apiUrl}/`, { ...stand });
  }

  updateStand(id: string, stand: StandModelExpectBackend, force: boolean): Observable<StandModel> {
    return this.http.patch<StandModel>(`${this.apiUrl}/${id}`, { ...stand, force });
  }

  getNextMetaData(): Observable<BasicMetaData> {
    return this.http.get<BasicMetaData>(`${this.apiUrl}/next-meta-data`);
  }

  getStandTemplateList(): Observable<TemplateModel[]> {
    return this.http.get<TemplateModel[]>(`${this.apiUrl}/stand-template-list`);
  }


  updateTemplate(id: string, template: TemplateExpectBackend, name: string): Observable<TemplateModel> {
    return this.http.patch<TemplateModel>(`${this.apiUrl}/stand-template-list/${id}`, { ...template, name });
  }

  createTemplate(name: string, template: TemplateExpectBackend): Observable<TemplateModel> {
    return this.http.post<TemplateModel>(`${this.apiUrl}/stand-template-list/`, { ...template, name });
  }


  deleteTemplate(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/stand-template-list/${id}`);
  }

  acceptStand(standId: UUID): Observable<StandModel> {
    return this.http.post<StandModel>(`${this.apiUrl}/accept-stand/${standId}`, {});
  }

  readyToPickUpStand(standId: UUID): Observable<StandModel> {
    return this.http.post<StandModel>(`${this.apiUrl}/ready-to-pick-up/${standId}`, {});
  }

  getStandsCount(): Observable<StandsCountModel> {
    return this.http.get<StandsCountModel>(`${this.apiUrl}/accepted-stands-count`);
  }


}
